import { useState, useRef } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUpload, faFile } from '@fortawesome/free-solid-svg-icons'
import { useUploadToExistingPolicyDocument } from 'api/hooks/use-policy-documents'

const PolicyUploadModal = ({ policyDocument, onHide, onSuccess }) => {
  const [file, setFile] = useState(null)
  const [error, setError] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef(null)

  const { mutate: upload, isLoading } = useUploadToExistingPolicyDocument({
    options: {
      onSuccess: () => {
        onSuccess?.()
        onHide()
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Upload failed. Please try again.')
      }
    }
  })

  const handleFile = (selected) => {
    if (!selected) return
    if (selected.type !== 'application/pdf') {
      setError('Please select a PDF file.')
      return
    }
    setError(null)
    setFile(selected)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    handleFile(e.dataTransfer.files[0])
  }

  const handleUpload = () => {
    if (!file || isLoading) return
    upload({ policyDocumentId: policyDocument.id, file })
  }

  return (
    <Modal show centered onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title className="fs-6 fw-bold">Upload &amp; Extract — {policyDocument.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="py-2">
            {error}
          </Alert>
        )}
        <div
          className={`border rounded p-4 text-center${dragOver ? ' border-primary bg-light' : ''}`}
          style={{ cursor: 'pointer', borderStyle: 'dashed' }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {file ? (
            <>
              <FontAwesomeIcon icon={faFile} className="text-primary mb-2" size="2x" />
              <p className="mb-0 small fw-bold">{file.name}</p>
              <p className="mb-0 text-muted small">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faUpload} className="text-muted mb-2" size="2x" />
              <p className="mb-1 small fw-bold">Drop your PDF here or click to browse</p>
              <p className="mb-0 text-muted small">PDF only, max 25MB</p>
            </>
          )}
        </div>
        <Form.Control
          type="file"
          accept="application/pdf"
          ref={fileInputRef}
          onChange={(e) => handleFile(e.target.files[0])}
          className="d-none"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" size="sm" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleUpload} disabled={!file || isLoading}>
          {isLoading ? (
            <Spinner as="span" animation="border" size="sm" className="me-1" />
          ) : (
            <FontAwesomeIcon icon={faUpload} className="me-1" />
          )}
          Upload &amp; Extract
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default PolicyUploadModal
